import React from 'react';
import { Route } from "react-router-dom";
/** @jsx jsx */
import { jsx } from '@emotion/core';
import css from '@emotion/css/macro';
import styled from '@emotion/styled/macro';
import NavBarItem, { NavMenuItemWrapAnchor, NavMenuItemInside } from "./NavbarItem"
import theme from '../../utils/theme'
import { theme as HeaderTheme } from "../Header"
import { mdiMenu, mdiFlashOutline, mdiAccountBoxOutline, mdiFileDocumentEditOutline, mdiEmailMultipleOutline, mdiEmailOutline, mdiClipboardFlowOutline, mdiInformationVariant, mdiInformationOutline, mdiAccountGroupOutline, mdiCashUsdOutline, mdiFormTextbox, mdiMessageTextOutline, mdiBugle, mdiAt, mdiTune } from '@mdi/js'
import Icon from '@mdi/react'

const NavbarTheme = {
  width: '0 0 240px;',
  width__collapsed: '0 0 56px;',
  shadow: 'rgba(0, 0, 0, 0.25) 1px 0px 2px 0px, rgba(0, 0, 0, 0.25) 4px 0px 8px -2px',
  padding: '12px 0px',
  backgroundColor: theme.surfaceColor,
  navMenuGroup: {
    padding: '0.75rem 0px',
    padding__first: '0'
  },
  footerGroup: {
    height: '52px'
  }
}
const Nav = styled.nav`
  grid-area: 2 / 1 / 3 / 2;
  box-shadow: ${NavbarTheme.shadow};
  flex: ${({ collapsed }) => (collapsed ? NavbarTheme.width__collapsed : NavbarTheme.width)};
  height: 100%;
  padding: ${NavbarTheme.padding};
  background-color: ${theme.color.elevatedSurface};
  display: flex;
  flex-direction: column;
  left: 0;
  z-index: 99;
  top: ${HeaderTheme.height};
  transition: flex 250ms cubic-bezier(0.4, 0, 0.2, 1) 0s;
  overflow: hidden auto;
  &::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    background-color: rgba(0,173,158,0.05);
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0,173,158,0.1);
  }
  &:hover::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  &::-webkit-scrollbar-button {
    display: none;
  }
`
const NavContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
`
const ContentTop = styled.div`
  height: calc(100% - ${NavbarTheme.footerGroup.height});
  display: flex;
  flex-direction: column;
`
const NavMenuGroup = styled.div`
  padding: ${NavbarTheme.navMenuGroup.padding};
  display: flex;
  flex-direction: column;
  &:first-of-type {
    padding-top: ${NavbarTheme.navMenuGroup.padding__first};
  }
`
function NavMenuGroupCMS(props) {
  if (window.location.pathname === '/cms/') {
    return (
      <NavMenuGroup>
        <NavBarItem label='Workflow (Drafts)' link='/cms/#/workflow' icon={mdiClipboardFlowOutline} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='About Pages' link='/cms/#/collections/about' icon={mdiInformationVariant} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='Information Pages' link='/cms/#/collections/information' icon={mdiInformationOutline} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='Member Pages' link='/cms/#/collections/members' icon={mdiAccountGroupOutline} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='Payment Pages' link='/cms/#/collections/payments' icon={mdiCashUsdOutline} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='Signup Forms' link='/cms/#/collections/signups' icon={mdiFormTextbox} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='Contact Forms' link='/cms/#/collections/contact' icon={mdiMessageTextOutline} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='Posts/Announcements' link='/cms/#/collections/posts' icon={mdiBugle} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='Email Newsletters' link='/cms/#/collections/email-newsletters' icon={mdiEmailOutline} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='Standalone Newsletters' link='/cms/#/collections/email-standalone' icon={mdiAt} collapsed={props.collapsed}></NavBarItem>
        <NavBarItem label='Site Settings' link='/cms/#/collections/settings' icon={mdiTune} collapsed={props.collapsed}></NavBarItem>
      </NavMenuGroup>
    );
  } else {
    return null;
  };
};

function useStickyState(defaultValue, key) {
  const [value, setValue] = React.useState(() => {
    const stickyValue = window.localStorage.getItem(key);
    return stickyValue !== null
      ? JSON.parse(stickyValue)
      : defaultValue;
  });
  React.useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

const NavMenuTitle = styled.span`
  opacity: ${({ collapsed }) => (collapsed ? "0" : "1")};
  transition: opacity 200ms ease 0s;
  white-space: nowrap;
`;

function NavBar(props) {
  const [collapsed, setCollapsed] = useStickyState(true, "collapsed");
  return <Nav collapsed={collapsed}>
    <NavContent>
      <NavMenuGroup>
        <NavMenuItemWrapAnchor css={css`color: white; font-family: ${theme.headlineFont}; font-size: 1.04rem; font-weight: 600; pointer-events: none;`}>
        <svg xmlns="http://www.w3.org/2000/svg" width="36px" height="36px" viewBox="0 0 36 36" version="1.1" css={css`margin: 0 0.75rem 0 -2px; color: ${theme.color.primary[1000]}; min-width: 36px; border-radius: ${theme.radius}`}>
          <path d="M36 0L0 0L0 36L36 36L36 0ZM25.3427 4C25.0604 4.28753 24.842 4.6984 24.4993 4.87124C24.1526 5.07364 23.7184 5.3844 23.3778 5.41808C22.669 5.72954 21.7491 5.86405 21.0421 6.08541C20.9284 6.11261 20.8102 6.14774 20.6891 6.18375C20.2917 6.30191 19.8621 6.42965 19.4476 6.3185C19.3319 6.33502 19.2078 6.33637 19.0824 6.33774C18.7143 6.34175 18.3358 6.34587 18.1318 6.73421C17.6178 6.97429 16.9804 7.10447 16.3875 7.09568C16.3094 7.04703 16.3716 6.98917 16.4422 6.92344C16.5411 6.83134 16.6566 6.72379 16.4264 6.60445C16.3643 6.54054 16.2825 6.48206 16.1997 6.42291C15.9807 6.26626 15.7551 6.10495 15.8703 5.8259C15.9913 5.7621 16.1459 5.71214 16.3062 5.66032C16.7559 5.51494 17.2508 5.35495 17.1772 4.83417C16.848 4.75483 16.5066 4.87248 16.1712 4.98808C15.9427 5.06684 15.7169 5.14466 15.4996 5.15856C15.0208 4.30063 13.858 3.90119 12.9507 4.25952C12.9202 4.28987 12.8891 4.3206 12.8576 4.35173C12.3441 4.85859 11.7243 5.4704 12.0239 6.26152C12.1891 6.72743 11.872 6.91663 11.544 7.11228C11.3905 7.2038 11.2347 7.29674 11.1248 7.42008C10.6024 7.87495 11.3825 8.03033 11.7458 7.76301C11.8416 7.73151 11.9441 7.67349 12.0479 7.61471C12.324 7.45839 12.6095 7.29676 12.8024 7.61472C13.101 7.76257 13.4737 8.16242 13.0342 8.32839C13.109 8.5542 12.9507 8.73245 12.7898 8.91372C12.6905 9.02554 12.5901 9.13852 12.5429 9.26451C12.1644 9.73538 12.0059 10.3658 11.718 10.8958C11.7214 11.2282 11.6404 11.5614 11.5589 11.8968C11.4465 12.3594 11.3331 12.8258 11.439 13.2993C11.4618 14.4445 11.5494 15.5999 11.9641 16.6706C12.0829 16.8874 12.2311 17.257 12.4952 17.4393C12.575 17.7957 12.4706 18.1621 12.3691 18.5188C12.2691 18.87 12.1718 19.2116 12.2556 19.5247C12.3221 19.5702 12.3886 19.6115 12.4528 19.6515C12.8081 19.8724 13.093 20.0495 12.9217 20.6358C12.8289 21.2174 12.9475 21.6337 13.0732 22.075C13.1506 22.3468 13.2308 22.6281 13.2658 22.9634C13.4596 23.6794 13.5002 24.4753 13.238 25.1971C13.3549 25.4285 13.2207 25.5974 13.0852 25.7679C12.9591 25.9265 12.8318 26.0866 12.9044 26.3C12.8115 27.2454 12.8657 28.2094 13.0341 29.1362C13.0712 29.4731 13.1813 29.805 13.2931 30.142C13.4666 30.6651 13.6443 31.2005 13.5592 31.7858C13.1732 32.1876 13.1096 33.1281 13.8127 33.2328C14.0003 33.3209 14.188 33.3177 14.3737 33.3146C14.6182 33.3105 14.8592 33.3064 15.0918 33.5109C15.5757 33.8408 16.1942 34.0683 16.6674 33.9187C17.3271 33.7863 17.8721 33.374 18.3635 32.9548C18.7795 32.416 17.8845 32.0818 17.4645 32.2041C16.6029 32.0241 15.5999 31.6657 15.3235 30.7304C15.0795 30.0147 15.1361 29.2668 15.1923 28.5246C15.2187 28.176 15.245 27.8286 15.2401 27.4864C15.4953 26.9569 15.5855 26.3278 15.6386 25.781C16.1805 25.2957 16.6799 24.5856 16.6581 23.779C16.7164 23.1048 17.0206 22.473 17.1772 21.9253C17.2182 21.3702 17.5943 20.859 17.6406 20.2755C17.7471 19.9173 18.3327 19.831 18.3913 19.5804C18.4255 19.0713 18.3145 18.5542 18.2041 18.0402C18.124 17.667 18.0442 17.2954 18.0206 16.9296C17.9779 16.7087 17.9251 16.4895 17.8724 16.2703C17.7333 15.6926 17.5943 15.1153 17.6406 14.5105C17.6139 14.2467 17.6069 13.9744 17.5998 13.6997C17.5839 13.0806 17.5676 12.4495 17.3255 11.8782C17.2907 11.7562 17.2286 11.6356 17.1667 11.5152C17.0503 11.2887 16.9342 11.063 17.0011 10.8309C17.8494 10.4757 18.8728 10.4204 19.5221 9.70013C19.8993 9.35473 19.7693 8.74836 19.9855 8.34693C21.1489 8.09867 22.2872 7.79645 23.3871 7.55911C23.8926 7.49529 24.3876 7.21435 24.3562 6.62762C24.4154 6.43052 24.2681 6.26304 24.151 6.12989C24.0067 5.96582 23.9083 5.85391 24.2985 5.80298C24.8494 5.6909 25.4833 5.84923 25.8062 6.21518C26.1867 5.98722 25.9236 5.10525 25.7227 4.61172C25.653 4.38564 25.4813 4.15108 25.3427 4ZM23.6374 7.0679C23.95 6.98919 23.9595 6.3081 23.4891 6.31715C22.5895 6.50056 21.477 6.75706 20.7024 6.95835C20.0444 6.86484 19.4893 7.48133 20.0597 7.96695C21.2377 7.65117 22.4695 7.41388 23.6374 7.0679ZM17.9002 7.18829C18.5051 7.67619 17.5215 8.09468 17.1216 7.55903C16.9522 7.39377 17.2817 7.33254 17.5663 7.27965C17.7173 7.25161 17.8555 7.22591 17.9002 7.18829ZM16.8657 7.71532C16.7894 7.65747 16.717 7.60256 16.6477 7.55911C16.436 7.58496 16.4821 7.70209 16.5341 7.8345C16.5805 7.95239 16.6316 8.08239 16.5098 8.17083C16.3991 8.34665 16.1075 8.35347 15.8505 8.35949C15.5442 8.36666 15.2872 8.37267 15.4439 8.66206C15.7495 8.55306 16.0366 8.58462 16.3067 8.61429C16.5197 8.6377 16.722 8.65993 16.9142 8.61105C16.8566 8.63458 16.9058 8.62573 17.0097 8.59685C16.9643 8.63282 16.9336 8.6589 16.9142 8.67714C16.9515 8.65534 16.9884 8.6277 17.025 8.59255C17.4213 8.48058 18.5072 8.11122 17.7703 8.07815C17.4098 8.12786 17.1167 7.90568 16.8657 7.71532ZM17.025 8.59255C17.0356 8.58237 17.0461 8.57157 17.0567 8.56009C17.0395 8.57335 17.0239 8.58559 17.0097 8.59685C17.0147 8.59546 17.0198 8.59403 17.025 8.59255Z" id="BuglerSquare" fill="CurrentColor" fillRule="evenodd" stroke="none"/>
        </svg>
          <NavMenuTitle collapsed={collapsed}>Content Portal</NavMenuTitle>
        </NavMenuItemWrapAnchor>
      </NavMenuGroup>
      <ContentTop>
        <NavMenuGroup>
          <NavBarItem onClick={() => setCollapsed(!collapsed)} label='Home' link='/' icon={mdiFlashOutline} collapsed={collapsed}></NavBarItem>
          <NavBarItem label='Content Manager' link='/cms/' icon={mdiFileDocumentEditOutline} collapsed={collapsed}></NavBarItem>
          <NavBarItem label='Emails' link='/emails/' icon={mdiEmailMultipleOutline} collapsed={collapsed}></NavBarItem>
          <NavBarItem label='Contacts' link='/contacts/' icon={mdiAccountBoxOutline} collapsed={collapsed}></NavBarItem>
        </NavMenuGroup>
        <Route path='/cms' render={(props) => <NavMenuGroupCMS {...props} collapsed={collapsed} />} />
      </ContentTop>
      <div>
        <NavMenuGroup>
          <NavMenuItemWrapAnchor onClick={() => setCollapsed(!collapsed)}>
            <NavMenuItemInside>
            <Icon path={mdiMenu} title="Toggle Menu" size={0.8336} css={css`margin: 0.375rem; min-width: 1.2504rem;`} />
            </NavMenuItemInside>
          </NavMenuItemWrapAnchor>
        </NavMenuGroup>
      </div>
    </NavContent>
  </Nav>
}


export { NavBar as default, NavbarTheme as theme }; 